import { z } from "zod";

import type { AvailabilityAppointmentType } from "@/shared";
import { usPostalCodeSchema } from "@/shared/models/address";
import { AppointmentType } from "@/shared/models/appointment";
import { PersonGender } from "@/shared/models/person";
import { EMPTY_GEOCODED_ADDRESS } from "@/src/components/address/types";
import {
  isValidOnlineSchedulingPhone,
  ONLINE_SCHEDULING_PHONE_ERROR_MESSAGE,
  sanitizePatientText,
  requiredOnlineSchedulingInsuranceSchema,
  type OnlineSchedulingLocation,
  type OnlineSchedulingProvider,
} from "@/src/lib/scheduling/online-scheduling-schemas";
import { validateState } from "@/src/lib/us-states";
import { isMinorDateOfBirth as isMinorDateOfBirthUtil } from "@/src/lib/utils/date-and-phone-utils";

export type { OnlineSchedulingLocation, OnlineSchedulingProvider };

export enum SchedulingStep {
  DETAILS = "details",
  DONE = "done",
  REVIEW = "review",
  TIME = "time",
  VISIT = "visit",
}

export const SCHEDULING_STEPS = [
  SchedulingStep.DETAILS,
  SchedulingStep.VISIT,
  SchedulingStep.TIME,
  SchedulingStep.REVIEW,
] as const;
export type SchedulingStepId = (typeof SCHEDULING_STEPS)[number];

/**
 * Shared styling for interactive controls (inputs, textarea, select triggers, option cards) so they
 * read as crisp white cards with a visible border against the off-white panel — in both light and
 * dark themes. `dark:border-border` overrides the primitives' hardcoded dark border.
 */
export const FIELD_CLASSNAME = "border-border bg-background dark:border-border";

export const STEP_LABELS: Record<SchedulingStepId, string> = {
  [SchedulingStep.VISIT]: "Visit details",
  [SchedulingStep.DETAILS]: "Patient details",
  [SchedulingStep.TIME]: "Date & time",
  [SchedulingStep.REVIEW]: "Review",
};

const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Gender values offered by the details-step dropdown, in display order. Covers every real
 * `PersonGender` identity a patient can select; `Unknown` is the internal "not specified" sentinel,
 * not a selectable option, so it's intentionally excluded.
 */
export const GENDER_OPTIONS = [PersonGender.MALE, PersonGender.FEMALE, PersonGender.OTHER] as const;

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

function patientNameSchema(message: string) {
  return z
    .string()
    .trim()
    .min(1, message)
    .max(80)
    .refine((value) => sanitizePatientText(value, 80).length > 0, message);
}

/**
 * Client-side schema powering the react-hook-form details step. Validation only — patient text is
 * sanitized at the API's Zod boundary (`onlineSchedulingBookRequestSchema`), so we don't `.transform()`
 * here (a transforming resolver changes react-hook-form's value types and can break the controlled form).
 */
export const patientDetailsFormSchema = z
  .object({
    firstName: patientNameSchema("Enter your first name"),
    lastName: patientNameSchema("Enter your last name"),
    dateOfBirth: z
      .string()
      .min(1, "Enter your date of birth")
      .refine((value) => !Number.isNaN(Date.parse(value)) && value <= todayIso(), "Enter a valid date of birth"),
    phone: z.string().refine(isValidOnlineSchedulingPhone, ONLINE_SCHEDULING_PHONE_ERROR_MESSAGE),
    gender: z.string().refine((value) => (GENDER_OPTIONS as readonly string[]).includes(value), "Select a gender"),
    email: z
      .string()
      .trim()
      .min(1, "Enter your email")
      .refine((value) => !value || emailPattern.test(value), "Enter a valid email"),
    homeAddress: z.object({
      line1: z.string().trim().min(1, "Enter your street address").max(120),
      line2: z.string().trim().max(120),
      city: z.string().trim().min(1, "Enter your city").max(80),
      state: z.string().refine((value) => validateState(value) !== null, "Select a state"),
      postalCode: z.string().trim().pipe(usPostalCodeSchema),
      placeId: z.string().nullable(),
    }),
    selfPay: z.boolean().optional(),
    insurancePayer: z.string().trim().max(120),
    insuranceMemberId: z.string().trim().max(60),
    // Collected only for patients under 18; required conditionally in the superRefine below.
    guardianFirstName: z.string().trim().max(80),
    guardianLastName: z.string().trim().max(80),
    guardianPhone: z.string().trim().max(40),
  })
  .superRefine((data, ctx) => {
    if (!data.selfPay && data.insuranceMemberId && !data.insurancePayer) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Enter your insurance carrier when providing a member ID",
        path: ["insurancePayer"],
      });
    }

    // Under-18 patients must be accompanied by a parent/legal guardian, so capture their
    // guardian's name and phone. Only enforced once a minor date of birth is entered.
    if (isMinorDateOfBirthUtil(data.dateOfBirth)) {
      if (!data.guardianFirstName) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Enter the guardian's first name",
          path: ["guardianFirstName"],
        });
      }

      if (!data.guardianLastName) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Enter the guardian's last name",
          path: ["guardianLastName"],
        });
      }

      if (!isValidOnlineSchedulingPhone(data.guardianPhone)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Enter a valid 10-digit guardian phone number",
          path: ["guardianPhone"],
        });
      }
    }
  });

export type PatientDetailsDraft = z.infer<typeof patientDetailsFormSchema>;

export function getPatientDetailsFormSchema(requireInsurance: boolean, allowSelfPay = false, isRescheduling = false) {
  const schema = isRescheduling
    ? patientDetailsFormSchema.safeExtend({
        email: z.string(),
        gender: z.string(),
        homeAddress: z.object({
          line1: z.string(),
          line2: z.string(),
          city: z.string(),
          state: z.string(),
          postalCode: z.string(),
          placeId: z.string().nullable(),
        }),
      })
    : patientDetailsFormSchema;

  return schema.superRefine((data, ctx) => {
    if (data.selfPay && allowSelfPay) return;
    if (!requireInsurance) return;

    const insurance = requiredOnlineSchedulingInsuranceSchema.safeParse({
      payer: data.insurancePayer,
      memberId: data.insuranceMemberId,
    });

    if (!insurance.success) {
      for (const issue of insurance.error.issues) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: issue.message,
          path: [issue.path[0] === "payer" ? "insurancePayer" : "insuranceMemberId"],
        });
      }
    }
  });
}

export const EMPTY_PATIENT_DETAILS: PatientDetailsDraft = {
  firstName: "",
  lastName: "",
  dateOfBirth: "",
  phone: "",
  gender: "",
  email: "",
  homeAddress: { ...EMPTY_GEOCODED_ADDRESS },
  selfPay: false,
  insurancePayer: "",
  insuranceMemberId: "",
  guardianFirstName: "",
  guardianLastName: "",
  guardianPhone: "",
};

/** New self-schedulers default to a new-patient visit; returning patients to a check-in. */
export function appointmentTypeForPatient(isReturningPatient: boolean): AvailabilityAppointmentType {
  return isReturningPatient ? AppointmentType.EXISTING_PATIENT_CHECKIN : AppointmentType.NEW_PATIENT_CHECKIN;
}

/** "City, ST" for a location's address, omitting whichever part is missing. */
export function formatLocality(location: Pick<OnlineSchedulingLocation, "city" | "state">): string {
  return [location.city, location.state].filter(Boolean).join(", ");
}

/** Strip pipe/newline chars from patient-entered visit reason before it hits review or the API. */
export function sanitizeReasonForVisit(reason: string): string {
  return sanitizePatientText(reason, 200);
}

/** Whether a "YYYY-MM-DD" date of birth is under 18 today. */
export function isMinorDateOfBirth(dateOfBirth: string): boolean {
  return isMinorDateOfBirthUtil(dateOfBirth);
}
